import { fetchAPI } from "./utils";

export interface ExportUser {
    id: string;
    email: string;
    communicationEmail: string | null;
    communicationEmailAddedAt: string | null;
    firstname: string;
    lastname: string;
    phone: string | null;
    createdAt: string;
}

export interface ExportUsersResponse {
    users: ExportUser[];
    pagination: {
        page: number;
        limit: number;
        totalCount: number;
        totalPages: number;
    };
}

export interface ExportTrack {
    id: string;
    name: string;
}

export interface GetExportUsersParams {
    token: string;
    page?: number;
    limit?: number;
    trackId?: string;
    startDate?: string;
    endDate?: string;
    commEmailStartDate?: string;
    commEmailEndDate?: string;
    search?: string;
    hideAnonymous?: boolean;
}

export interface ExportCSVParams {
    token: string;
    trackId?: string;
    startDate?: string;
    endDate?: string;
    commEmailStartDate?: string;
    commEmailEndDate?: string;
    search?: string;
    hideAnonymous?: boolean;
}

/**
 * Get paginated list of users for export
 */
export const getExportUsers = async ({
    token,
    page = 1,
    limit = 10,
    trackId,
    startDate,
    endDate,
    commEmailStartDate,
    commEmailEndDate,
    search,
    hideAnonymous,
}: GetExportUsersParams): Promise<ExportUsersResponse> => {
    return fetchAPI<ExportUsersResponse>(
        "/get-export-users",
        "POST",
        { page, limit, trackId, startDate, endDate, commEmailStartDate, commEmailEndDate, search, hideAnonymous },
        token,
    );
};

/**
 * Get list of tracks for filter dropdown
 */
export const getCompanyTracksForExport = async (token: string): Promise<ExportTrack[]> => {
    return fetchAPI<ExportTrack[]>("/get-company-tracks-for-export", "GET", undefined, token);
};

/**
 * Export users to CSV - triggers download
 */
export const exportUsersToCSV = async ({
    token,
    trackId,
    startDate,
    endDate,
    commEmailStartDate,
    commEmailEndDate,
    search,
    hideAnonymous,
}: ExportCSVParams): Promise<void> => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!baseUrl) {
        throw new Error("REACT_APP_API_BASE_URL environment variable is not defined");
    }

    const response = await fetch(`${baseUrl}/export-users-csv`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            trackId,
            startDate,
            endDate,
            commEmailStartDate,
            commEmailEndDate,
            search,
            hideAnonymous,
        }),
    });

    if (!response.ok) {
        throw new Error(`Export failed with status ${response.status}`);
    }

    // Get the CSV content
    const csvContent = await response.text();

    // Create a blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = "users-export.csv";
    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
            filename = filenameMatch[1];
        }
    }

    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};
